/**
 * Cabinets, drawn.
 *
 * Everything here is built from parameters rather than loaded from a model library. Two
 * reasons, and the second is the one that matters: a downloaded cabinet model is somebody
 * else's licence, and it cannot take this maker's finishes. A shaker door has to be THIS
 * maker's chalk white in THIS maker's gloss, and the only way to guarantee that is to build
 * the door and hand it the material.
 *
 * Millimetres in, metres out. three.js is happier in metres and a kitchen is measured in
 * millimetres, so the conversion happens here, once, at the edge.
 *
 * ORIGIN: a cabinet's local origin is the bottom-left corner of its BACK, and it extends
 * toward +z into the room. That is not an arbitrary choice -- a run's origin is the wall
 * plane, so putting the cabinet's back there is what makes the whole cabinet stand IN the
 * room. Building it around the door face instead (which it was, at first) leaves the doors
 * flush with the plasterboard and the entire carcass inside the wall: right at a glance from
 * across the room, and obviously wrong the moment anybody looks at a corner.
 */
import * as THREE from 'three'
import { MM } from './units.js'
import { buildFridge, buildDishwasher, buildOven, buildAppliance } from './appliance.js'
import { stoneTexture, splashTexture, doorTexture } from './texture.js'

// Re-exported: scene.js has always asked build.js for the sink, and cutting one into a
// benchtop is still part of building a cabinet even though the code moved next to the fridge.
export { buildAppliance }

// MM lives in units.js now, because appliance.js needs it too and the two files
// importing each other for a constant is a cycle nobody should have to think about.
// Re-exported here: scene.js and touch.js have always asked build.js for it.
export { MM } from './units.js'

/** Reveal between a door and its neighbour. The shadow line that says "these are doors". */
export const REVEAL = 3

/**
 * A carcass, faced in door material on every side a person can see.
 *
 * A cabinet box was one material, and that material was white melamine -- which is what the
 * INSIDE of a cabinet is made of, and what the outside is only where nobody looks. The
 * result was a big white block wherever a run ended, wherever an overhead showed its
 * underside, and along the top of every wall cabinet: exactly the faces a maker supplies as
 * a finished end panel in the door colour, because they are the ones in the room.
 *
 * BoxGeometry takes its materials in the order +x, -x, +y, -y, +z, -z. The two ends, the top
 * and the bottom get the door; the front is behind the doors and the back is against the
 * wall, so those two stay melamine -- and on an open shelf, where the front is not behind a
 * door, melamine is exactly right: that is what the inside of the cupboard is.
 */
const faced = (look) => [
  look.doorMat, look.doorMat, look.doorMat, look.doorMat, look.carcass, look.carcass,
]

/**
 * A door face.
 *
 * Slab is a panel. Shaker is a frame with a recessed centre, and the recess is what catches
 * the light -- flat-shading it as one box loses the only thing that makes it a shaker.
 */
function doorFace(w, h, door) {
  const g = new THREE.Group()
  const thick = 18

  if (door.style === 'shaker') {
    const rail = 70
    // Frame, as four bars, so the centre can sit back from them.
    const bars = [
      [w, rail, 0, (h - rail) / 2],
      [w, rail, 0, -(h - rail) / 2],
      [rail, h - rail * 2, -(w - rail) / 2, 0],
      [rail, h - rail * 2, (w - rail) / 2, 0],
    ]
    for (const [bw, bh, x, y] of bars) {
      const m = new THREE.Mesh(new THREE.BoxGeometry(bw * MM, bh * MM, thick * MM))
      m.position.set(x * MM, y * MM, 0)
      m.castShadow = m.receiveShadow = true
      g.add(m)
    }
    // The centre panel, set back. 6mm is enough to read as a shadow at any sane zoom.
    const panel = new THREE.Mesh(
      new THREE.BoxGeometry((w - rail * 2) * MM, (h - rail * 2) * MM, (thick - 6) * MM))
    panel.position.z = -3 * MM
    panel.receiveShadow = true
    g.add(panel)
  } else if (door.style === 'vgroove') {
    // A panel with grooves routed down it. Drawn as a slab plus the shadow the grooves cast,
    // because at any real zoom a groove IS its shadow.
    const m = new THREE.Mesh(new THREE.BoxGeometry(w * MM, h * MM, thick * MM))
    m.castShadow = m.receiveShadow = true
    g.add(m)
    const pitch = 76
    const n = Math.max(2, Math.round((w - 40) / pitch))
    for (let i = 1; i < n; i++) {
      const groove = new THREE.Mesh(
        new THREE.BoxGeometry(5 * MM, (h - 24) * MM, 5 * MM))
      groove.position.set((-w / 2 + (w * i) / n) * MM, 0, (thick / 2 - 2) * MM)
      groove.receiveShadow = true
      g.add(groove)
    }
  } else {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w * MM, h * MM, thick * MM))
    m.castShadow = m.receiveShadow = true
    g.add(m)
  }

  return g
}

/** A handle. Small, and the thing a hand actually reaches for, so it is worth drawing. */
function handle(style, w, h, vertical) {
  if (style === 'none') return null
  const g = new THREE.Group()

  if (style === 'bar') {
    const len = Math.min(vertical ? h * 0.4 : w * 0.4, 220)
    const bar = new THREE.Mesh(
      new THREE.CylinderGeometry(6 * MM, 6 * MM, len * MM, 12))
    if (!vertical) bar.rotation.z = Math.PI / 2
    bar.castShadow = true
    g.add(bar)
    for (const s of [-1, 1]) {
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(4 * MM, 4 * MM, 26 * MM, 8))
      leg.rotation.x = Math.PI / 2
      const off = (len / 2 - 18) * MM * s
      leg.position.set(vertical ? 0 : off, vertical ? off : 0, -13 * MM)
      g.add(leg)
    }
    g.position.z = 13 * MM
  } else if (style === 'knob') {
    // A knob: one turned button on a short stem. Small, and the only handle that does not
    // care which way the door is hung, which is why it takes no `vertical`.
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(5 * MM, 5 * MM, 14 * MM, 10))
    stem.rotation.x = Math.PI / 2
    stem.position.z = 7 * MM
    g.add(stem)
    const knob = new THREE.Mesh(new THREE.CylinderGeometry(11 * MM, 9 * MM, 12 * MM, 14))
    knob.rotation.x = Math.PI / 2
    knob.position.z = 20 * MM
    knob.castShadow = true
    g.add(knob)
  } else {
    // A D-pull: two legs and a bar close to the face.
    const len = Math.min(vertical ? h * 0.28 : w * 0.28, 160)
    const bar = new THREE.Mesh(new THREE.BoxGeometry(
      vertical ? 8 * MM : len * MM, vertical ? len * MM : 8 * MM, 8 * MM))
    bar.castShadow = true
    g.add(bar)
    g.position.z = 20 * MM
  }
  return g
}

/**
 * One cabinet: carcass, doors or drawer fronts, handles.
 *
 * Returned as a group whose origin is the bottom-left corner of the cabinet's BACK, which is
 * where a run counts from -- see the note at the top of this file about why that is the back
 * and not the door.
 */
export function buildCabinet(item, spec, look, limits, opts = {}) {
  const g = new THREE.Group()
  const w = item.w
  /*
   * The cabinet's own size wins; the maker's default is only a fallback.
   *
   * This used to read the height and depth straight off `limits`, which made every overhead
   * in the range exactly 720 tall however it was described -- so a "450 overhead" and a "900
   * overhead" drew identically and the only real dimension in the tool was width. A range
   * without height is not a range.
   */
  const h = spec.h ?? (spec.kind === 'wall' ? limits.wallHeight : limits.baseHeight)
  const d = spec.d ?? (spec.kind === 'wall' ? limits.wallDepth : limits.baseDepth)

  g.userData = { itemId: item.id, specId: spec.id, w, h, d, kind: spec.kind }

  // The three things a kitchen maker does not make. The range carries the SPACE for each;
  // what stands in it has a size of its own, which is not the same number -- appliance.js.
  if (spec.role === 'fridge') { g.add(buildFridge(item, spec, look, limits)); return g }
  if (spec.role === 'dishwasher') { g.add(buildDishwasher(item, spec, look, limits)); return g }
  if (spec.role === 'oven') { g.add(buildOven(item, spec, look, limits)); return g }
  // A corner unit is an L, not a box, when the caller says which way the corner turns.
  if (spec.role === 'corner' && opts.corner) { g.add(buildCorner(item, spec, look, limits, opts.corner)); return g }
  if (spec.role === 'rangehood') { g.add(buildRangehood(w, h, d, look)); return g }
  if (item.filler) {
    // Cut from the same board as the doors. A maker's filler is meant to disappear, and in
    // melamine it did the opposite: a pale stripe between two sage cupboards.
    const f = new THREE.Mesh(new THREE.BoxGeometry(w * MM, h * MM, d * MM), look.doorMat)
    f.position.set(w / 2 * MM, h / 2 * MM, d / 2 * MM)
    f.castShadow = f.receiveShadow = true
    g.add(f)
    return g
  }

  // The carcass: back on the wall, front stopping short so the doors sit proud of it.
  const carcassDepth = d - 20
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(w * MM, h * MM, carcassDepth * MM), faced(look))
  body.position.set(w / 2 * MM, h / 2 * MM, carcassDepth / 2 * MM)
  body.castShadow = body.receiveShadow = true
  g.add(body)

  // The doors, on the front of it, 18mm thick and centred on their own thickness.
  const fronts = new THREE.Group()
  fronts.position.z = (carcassDepth + 9) * MM

  if (spec.drawers > 0) {
    const each = (h - REVEAL * (spec.drawers + 1)) / spec.drawers
    for (let i = 0; i < spec.drawers; i++) {
      const face = paint(doorFace(w - REVEAL * 2, each, look.door), look.doorMat)
      face.position.set(w / 2 * MM, (REVEAL + i * (each + REVEAL) + each / 2) * MM, 0)
      const hd = paint(handle(look.handle.style, w, each, false), look.handleMat)
      if (hd) { hd.position.y = (each / 2 - 60) * MM; face.add(hd) }
      fronts.add(face)
    }
  } else if (spec.doors > 0) {
    const each = (w - REVEAL * (spec.doors + 1)) / spec.doors
    for (let i = 0; i < spec.doors; i++) {
      const face = paint(doorFace(each, h - REVEAL * 2, look.door), look.doorMat)
      face.position.set((REVEAL + i * (each + REVEAL) + each / 2) * MM, h / 2 * MM, 0)
      const hd = paint(handle(look.handle.style, each, h, true), look.handleMat)
      if (hd) {
        /*
         * Handles go on the door's OPENING edge, 45mm in.
         *
         * A pair opens outward from the middle, so its two handles meet there -- that part
         * was always right. A single door has no such rule in the geometry, and every one
         * of them was hinged the same way, which left a run of singles with a handle stuck
         * hard against the join at one end of each door and half a metre of nothing at the
         * other. `hingeRight` alternates them along the run (scene.js counts), so
         * neighbouring singles mirror and their handles pair up at the join the way the
         * sink's two do. That is how a kitchen is actually hung.
         */
        const side = spec.doors === 2 ? (i === 0 ? 1 : -1) : (opts.hingeRight ? 1 : -1)
        hd.position.x = side * (each / 2 - 45) * MM
        face.add(hd)
      }
      fronts.add(face)
    }
  }

  // An open shelf is a carcass and a shelf. Drawing it as a doorless box loses the one thing
  // that makes it read as open rather than as a cabinet somebody forgot to finish.
  if (spec.role === 'open') {
    const shelf = new THREE.Mesh(
      new THREE.BoxGeometry((w - 36) * MM, 18 * MM, (d - 40) * MM), look.carcass)
    shelf.position.set(w / 2 * MM, h / 2 * MM, (d - 40) / 2 * MM)
    shelf.castShadow = shelf.receiveShadow = true
    g.add(shelf)
  }

  g.add(fronts)
  return g
}

/**
 * A rangehood, in whichever of the three shapes the kitchen was given.
 *
 * The range carries the SPACE (`w900h`) and this says what stands in it, which is why the
 * choice is a finish and not another cabinet: swapping it does not move a millimetre of the
 * layout.
 *
 *   canopy      the chimney kind, steel, on show. What this always drew.
 *   under       a slim hood with a real cupboard above it -- the version that keeps the
 *               storage, and the one most kitchens this size actually get.
 *   concealed   hidden behind a cabinet front, so the wall reads as unbroken cupboards and
 *               only the shadow line under it gives it away.
 */
function buildRangehood(w, h, d, look) {
  const g = new THREE.Group()
  const style = look.fittings?.rangehood?.style ?? 'canopy'
  const inner = d - 20

  if (style === 'canopy') {
    const hood = new THREE.Mesh(new THREE.BoxGeometry(w * MM, 60 * MM, inner * MM),
      look.appliance.steel)
    hood.position.set(w / 2 * MM, 30 * MM, inner / 2 * MM)
    hood.castShadow = true
    g.add(hood)
    const chimney = new THREE.Mesh(
      new THREE.BoxGeometry(Math.min(w, 500) * MM, (h - 60) * MM, 300 * MM), look.appliance.steel)
    chimney.position.set(w / 2 * MM, (60 + (h - 60) / 2) * MM, 150 * MM)
    g.add(chimney)
    return g
  }

  // Both of the others are a cupboard with a hood under it; what differs is whether the hood
  // is steel and on show or a door front like everything either side of it.
  const hoodH = style === 'under' ? 130 : 90
  const hood = new THREE.Mesh(new THREE.BoxGeometry(w * MM, hoodH * MM, inner * MM),
    style === 'under' ? look.appliance.steel : look.doorMat)
  hood.position.set(w / 2 * MM, hoodH / 2 * MM, inner / 2 * MM)
  hood.castShadow = hood.receiveShadow = true
  g.add(hood)
  // The shadow line: the gap a hood pulls through, and the only part of a concealed one
  // anybody ever sees.
  const slot = new THREE.Mesh(new THREE.BoxGeometry((w - 40) * MM, 12 * MM, 8 * MM),
    look.appliance.glass)
  slot.position.set(w / 2 * MM, 8 * MM, (inner + 6) * MM)
  g.add(slot)

  const cupH = h - hoodH - REVEAL
  if (cupH > 120) {
    const body = new THREE.Mesh(
      new THREE.BoxGeometry(w * MM, cupH * MM, inner * MM), faced(look))
    body.position.set(w / 2 * MM, (hoodH + REVEAL + cupH / 2) * MM, inner / 2 * MM)
    body.castShadow = body.receiveShadow = true
    g.add(body)
    // A lift-up door, which is what goes above a hood: one front, hinged at the top.
    const door = paint(doorFace(w - REVEAL * 2, cupH - REVEAL, look.door), look.doorMat)
    door.position.set(w / 2 * MM, (hoodH + REVEAL + cupH / 2) * MM, (inner + 9) * MM)
    g.add(door)
    const hd = paint(handle(look.handle.style, w, cupH, false), look.handleMat)
    if (hd) {
      hd.position.set(w / 2 * MM, (hoodH + REVEAL + 60) * MM, (inner + 21) * MM)
      g.add(hd)
    }
  }
  return g
}

/**
 * A corner unit, as the L it is.
 *
 * A corner cabinet reaches along BOTH walls -- 900 by 900 in this range -- with a door on
 * the outer half of its main arm and blank panels everywhere else, because the space behind
 * the corner is reached through that one door. Drawn as a box, which is what this did, it
 * was correct in the schedule, correct in the review, and wrong in every picture.
 *
 * In the cabinet's own coordinates (origin at the bottom-left of its back, +x along the run,
 * +z into the room), with R the return and d the depth:
 *
 *     main arm    x in [0, w],      z in [0, d]
 *     return arm  x in [w - d, w],  z in [d, R]
 *
 * The return goes at the +x END because that is the only place it can be: `extents` shortens
 * the START of the run after the corner, and the generator only ever places a corner unit at
 * `prev.end - corner.w`. So the corner is always at the far end of the run coming into it,
 * and the return always reaches forward past the depth into the wall that follows.
 *
 * `theta` is the interior angle. Anything that is not square gets the plain box: no stock
 * corner unit fits a splayed corner, which is what the review already tells people.
 */
export function buildCorner(item, spec, look, limits, { theta = Math.PI / 2, return: R = 900 } = {}) {
  const g = new THREE.Group()
  const w = item.w
  const h = spec.h ?? limits.baseHeight
  const d = spec.d ?? limits.baseDepth
  if (Math.abs(theta - Math.PI / 2) > 0.09) return buildBox(item, spec, look, limits)

  const carcassDepth = d - 20
  // The main arm, and the return standing behind it. Two boxes rather than an extruded L:
  // the same silhouette, half the triangles, and they shadow identically.
  const main = new THREE.Mesh(
    new THREE.BoxGeometry(w * MM, h * MM, carcassDepth * MM), faced(look))
  main.position.set(w / 2 * MM, h / 2 * MM, carcassDepth / 2 * MM)
  main.castShadow = main.receiveShadow = true
  g.add(main)

  /*
   * The return, and the panel that faces it.
   *
   * The return arm encloses the blind pocket behind the corner -- the space the next run
   * starts past, reached through the corner unit's own door. Its side is the ONE face of a
   * corner unit that a person in the room can see, and it was left as bare carcass, which
   * reads as a hole where a cabinet should be. A maker faces it in door material, because it
   * is a finished end, and that is what this does.
   */
  const backLen = Math.max(0, R - d)
  if (backLen > 1) {
    const ret = new THREE.Mesh(
      new THREE.BoxGeometry(carcassDepth * MM, h * MM, backLen * MM), faced(look))
    ret.position.set((w - carcassDepth / 2) * MM, h / 2 * MM, (d + backLen / 2) * MM)
    ret.castShadow = ret.receiveShadow = true
    g.add(ret)

    const facing = new THREE.Mesh(
      new THREE.BoxGeometry(18 * MM, (h - REVEAL * 2) * MM, backLen * MM), look.doorMat)
    facing.position.set((w - carcassDepth - 9) * MM, h / 2 * MM, (d + backLen / 2) * MM)
    facing.castShadow = facing.receiveShadow = true
    g.add(facing)

    /*
     * And the kick under it.
     *
     * A run's kick is one board along the RUN, and the corner's return arm is not on the
     * run -- it reaches down the next wall, into the stretch the next run starts past. So
     * the return stood on nothing: 340mm of missing toe kick at the inside corner of every
     * L, with the floor visible under the cabinet. Set back the same 60 as the run's, so
     * the two read as one continuous shadow line turning the corner.
     */
    // An overhead corner has no kick under it: it is hanging on the wall.
    if (spec.kind !== 'wall') {
      const SETBACK = 60
      // It has to REACH both kicks, or the corner is a notch instead of a corner. It starts
      // at the run's own kick plane (baseDepth - SETBACK, where the first run's board is) so
      // the two cross, and it stands at the same setback from the NEXT wall as that run's
      // kick will -- the same plane, so the two are one line turning the corner.
      const kz0 = limits.baseDepth - SETBACK
      const kickLen = d + backLen - kz0
      const kick = new THREE.Mesh(
        new THREE.BoxGeometry(1 * MM, limits.kickHeight * MM, kickLen * MM), look.kickMat)
      kick.position.set((w - limits.baseDepth + SETBACK) * MM, -limits.kickHeight / 2 * MM,
                        (kz0 + kickLen / 2) * MM)
      g.add(kick)
    }
  }

  /*
   * One door, on the half of the front nearest the room, and a blank panel beside it: that
   * is what a blind corner looks like, and it is why the spec says one door for 900mm.
   *
   * `doorFace` builds the SHAPE with placeholder materials and every caller paints it -- so
   * the first version of this drew a door and left it white, in a sage kitchen, which is
   * how it was spotted. `paint` is that step, named, so the next person to add a door face
   * cannot miss it.
   */
  const doorW = Math.min(w / 2, 450)
  const front = (carcassDepth + 9) * MM
  const door = paint(doorFace(doorW - REVEAL, h - REVEAL * 2, look.door), look.doorMat)
  door.position.set((doorW / 2) * MM, h / 2 * MM, front)
  g.add(door)
  const hd = paint(handle(look.handle.style, doorW, h, true), look.handleMat)
  if (hd) { hd.position.set((doorW - 60) * MM, h * 0.52 * MM, front + 12 * MM); g.add(hd) }

  const blankW = w - doorW
  if (blankW > 1) {
    const blank = new THREE.Mesh(
      new THREE.BoxGeometry((blankW - REVEAL) * MM, (h - REVEAL * 2) * MM, 18 * MM), look.doorMat)
    blank.position.set((doorW + blankW / 2) * MM, h / 2 * MM, front)
    blank.castShadow = true
    g.add(blank)
  }
  return g
}

/**
 * Give every mesh in a group the material it is meant to wear.
 *
 * `doorFace` and `handle` build geometry with placeholders, because the shape of a shaker
 * door does not depend on its colour and building one material per door would be a material
 * per cabinet. Painting is a separate step, and it used to be four copies of the same
 * traverse -- one of which I forgot, which is how a white door turned up in a sage kitchen.
 */
function paint(group, mat) {
  if (!group) return group
  group.traverse((m) => { if (m.isMesh) m.material = mat })
  return group
}

/** The plain rectangular carcass, for a corner that is not square. */
function buildBox(item, spec, look, limits) {
  const g = new THREE.Group()
  const w = item.w
  const h = spec.h ?? limits.baseHeight
  const d = (spec.d ?? limits.baseDepth) - 20
  const body = new THREE.Mesh(new THREE.BoxGeometry(w * MM, h * MM, d * MM), faced(look))
  body.position.set(w / 2 * MM, h / 2 * MM, d / 2 * MM)
  body.castShadow = body.receiveShadow = true
  g.add(body)
  const door = paint(doorFace(w - REVEAL, h - REVEAL * 2, look.door), look.doorMat)
  door.position.set(w / 2 * MM, h / 2 * MM, (d + 9) * MM)
  g.add(door)
  const hd = paint(handle(look.handle.style, w, h, true), look.handleMat)
  if (hd) { hd.position.set((w / 2 - 45) * MM, h * 0.52 * MM, (d + 21) * MM); g.add(hd) }
  return g
}

/**
 * Give a geometry's UVs a REAL SIZE, in metres.
 *
 * Every box and plane three.js makes runs its UVs 0..1 across each face, so a stone texture
 * on a 900 benchtop and the same texture on a 3600 one come out at four different sizes --
 * the veins smear along the long run and the grout lines on a splashback are whatever width
 * the wall happened to be. Scaling the UVs by (metres / span) makes one texture pixel the
 * same number of real millimetres wherever it lands, which is the difference between a
 * material and a wallpaper.
 *
 * `span` is what texture.js says its texture covers. Called on the geometry, not the
 * material, because the material is shared by every run and the sizes are not.
 */
export function sizeUv(geometry, metresX, metresY, span) {
  const uv = geometry.attributes.uv
  if (!uv || !span) return geometry
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, uv.getX(i) * (metresX / span), uv.getY(i) * (metresY / span))
  }
  uv.needsUpdate = true
  return geometry
}

/** The benchtop over a base run, with its overhang and a real edge. */
export function buildBenchtop(length, limits, mat, { back = 0 } = {}) {
  // `back` is the overhang BEHIND the run's origin, which only an island has: the seating
  // side people sit at. Zero by default, so a run against a wall is exactly what it was.
  const d = limits.baseDepth + limits.benchtopOverhang + back
  const geo = new THREE.BoxGeometry(length * MM, limits.benchtopThickness * MM, d * MM)
  // The stone runs at its own size along the top, however long the run is.
  if (mat.userData?.span) sizeUv(geo, length * MM, d * MM, mat.userData.span)
  const top = new THREE.Mesh(geo, mat)
  // Back on the wall like the cabinets under it, and running forward far enough to overhang.
  top.position.set(length / 2 * MM,
                   (limits.kickHeight + limits.baseHeight + limits.benchtopThickness / 2) * MM,
                   (d / 2 - back) * MM)
  top.castShadow = top.receiveShadow = true
  return top
}

/**
 * The materials for one look.
 *
 * Built once per finish rather than per cabinet, so changing a door colour is one material
 * update and not a scene rebuild -- which is what makes the finish picker feel instant
 * rather than like a reload.
 */
export function buildLook(style, colour, benchtop, handleSpec, splashback, kickColour, fittings = {}) {
  // Style and colour arrive separately and are married here: the profile decides the shape
  // of the door, the colour decides what it is made of.
  const door = { style: style.style, colour: colour.colour, gloss: colour.gloss,
                 name: `${style.name} ${colour.name}` }

  /*
   * A door, and what it is MADE of as well as what colour it is.
   *
   * The maps come from texture.js and are null in node, where there is no canvas -- which is
   * why every one of them is assigned conditionally rather than passed in the constructor: a
   * `map: null` is the same as no map, but `roughnessMap: undefined` on a material three.js
   * has already compiled is not, and the tests build looks without a DOM.
   *
   * A woodgrain colour arrives as a MAP, so the base colour goes white: multiplying a grain
   * texture that is already the right colour by the colour again doubles it and oak comes
   * out orange.
   */
  const doorTex = doorTexture(colour)
  const doorMat = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(doorTex.map ? 0xffffff : door.colour),
    roughness: 1 - door.gloss * 0.92,
    metalness: 0,
    clearcoat: door.gloss,
    clearcoatRoughness: (1 - door.gloss) * 0.6,
  })
  if (doorTex.map) doorMat.map = doorTex.map
  if (doorTex.roughnessMap) doorMat.roughnessMap = doorTex.roughnessMap

  const benchTex = stoneTexture(benchtop)
  const benchMat = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(benchTex.map ? 0xffffff : benchtop.colour),
    roughness: 1 - benchtop.gloss * 0.85,
    metalness: 0,
    clearcoat: benchtop.gloss * 0.7,
  })
  if (benchTex.map) benchMat.map = benchTex.map
  if (benchTex.roughnessMap) benchMat.roughnessMap = benchTex.roughnessMap
  // Read back by buildBenchtop, which is the only place that knows how long the run is.
  benchMat.userData.span = benchTex.span
  const handleMat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(handleSpec.colour),
    roughness: handleSpec.style === 'bar' ? 0.35 : 0.3,
    metalness: 0.85,
  })
  const carcass = new THREE.MeshStandardMaterial({ color: 0xd8d2c8, roughness: 0.85 })
  const steel = new THREE.MeshStandardMaterial({ color: 0xb9bec2, roughness: 0.22, metalness: 0.9 })
  /*
   * Brushed stainless, for the BIG flat faces: a fridge door, a dishwasher front.
   *
   * `steel` above is nearly a mirror, which is right for a tap and a sink bowl -- small,
   * curved, catching a highlight. On a 900 x 1750 flat panel a mirror reflects whatever the
   * environment map has in that direction, which from most angles is the dark side of the
   * room, and the fridge renders as a black slab. Brushed steel is what an appliance front
   * actually is: rough enough to scatter, metallic enough to be metal.
   */
  const panel = new THREE.MeshStandardMaterial({ color: 0xc6cace, roughness: 0.42, metalness: 0.55 })
  const glass = new THREE.MeshPhysicalMaterial({ color: 0x14161a, roughness: 0.08, clearcoat: 1 })
  const ring = new THREE.MeshStandardMaterial({ color: 0x2b2b2e, roughness: 0.6, metalness: 0.4 })

  // "Match the benchtop" is a real and common choice, so it is literally the same stone.
  let splash = benchMat
  if (splashback?.colour) {
    const tex = splashTexture(splashback)
    splash = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(tex.map ? 0xffffff : splashback.colour),
      roughness: 1 - (splashback.gloss ?? 0.5) * 0.9,
      clearcoat: splashback.gloss ?? 0.5,
    })
    if (tex.map) splash.map = tex.map
    if (tex.roughnessMap) splash.roughnessMap = tex.roughnessMap
    splash.userData.span = tex.span
  }

  const kickMat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(kickColour?.colour ?? '#2A2724'), roughness: 0.88 })

  return { door, doorMat, benchMat, handleMat, carcass, handle: handleSpec,
           splash, kickMat, fittings, appliance: { steel, panel, glass, ring } }
}
